import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexPlotOptions,
  ApexResponsive,
  ApexTitleSubtitle,
  ApexXAxis,
  ApexYAxis,
  ChartComponent,
} from 'ng-apexcharts';

export {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexPlotOptions,
  ApexTitleSubtitle,
  ApexXAxis,
  ChartComponent,
};

// IQ bar color: single blue
export const IQ_CHART_COLORS = ['#8781FF'];

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  plotOptions: ApexPlotOptions;
  title: ApexTitleSubtitle;
  responsive: ApexResponsive[];
  colors: string[];
  legend: ApexLegend;
};

@Component({
  selector: 'app-horizontal-bar-graph',
  templateUrl: './horizontal-bar-graph.component.html',
  styleUrls: ['../../../../shared/analytics//analytics.scss'],
  encapsulation: ViewEncapsulation.None,
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HorizontalBarGraphComponent implements OnChanges {
  @ViewChild('chart') chart: ChartComponent;

  @Input() data: any[] = [];

  @Input() categories: string[] = [];

  @Input() chartName: string = '';

  @Output() changeDevice = new EventEmitter<string>();

  public chartOptions: ChartOptions;

  constructor() {
    this.chartOptions = {
      series: [],
      chart: {
        height: 350,
        type: 'bar',
        events: {
          dataPointSelection: (event, chartContext, config) => {
            this.onChangeDevice(config);
          },
        },
      },
      colors: IQ_CHART_COLORS,
      legend: {
        show: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          distributed: false,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -17,
        style: {
          colors: ['#374151'],
          fontSize: '11px',
          fontWeight: 'bold',
        },
        formatter: (value: number) => {
          if (this.chartName.includes('%')) {
            return `${value.toLocaleString('en-US')}%`;
          }
          return value.toLocaleString('en-US');
        },
      },
      xaxis: {
        categories: this.categories,
      },
      yaxis: {
        labels: {
          formatter: (value: number) => {
            if (this.chartName.includes('%')) {
              return `${value.toLocaleString('en-US')}%`;
            }
            return value.toLocaleString('en-US');
          },
        },
      },
      title: {
        text: '',
        align: 'center',
        margin: 10,
        offsetX: 0,
        offsetY: 0,
        floating: false,
      },
      responsive: [],
    };

    this.applyResponsiveOptions();
  }

  ngOnChanges(): void {
    // Update chart options when @Input properties change
    this.chartOptions.xaxis = { categories: this.categories };
    this.chartOptions.series = this.data;
    this.chartOptions.title.text = '';
    this.applyResponsiveOptions();
  }

  private applyResponsiveOptions() {
    this.chartOptions.responsive = [
      {
        breakpoint: 768,
        options: {
          chart: { toolbar: { show: false } },
          title: {
            text: '',
            align: 'center',
            offsetY: 0,
          },
        },
      },
    ];
  }

  onChangeDevice(config: { dataPointIndex: number }): void {
    const selectedPointIndex = config.dataPointIndex;
    this.changeDevice.emit(
      this.chartOptions.xaxis.categories[selectedPointIndex] as string,
    );
  }
}
